import {
  AvatarImageProps,
  Box,
  Avatar as ChakraAvatar,
  AvatarGroup as ChakraAvatarGroup,
  useAvatarContext,
} from '@chakra-ui/react';

import type { PresenceStatus } from '#features/presence/model/presenceStore';
import { pickAvatarPalette } from '../utils/avatarFallbackColor';
import { Image } from './Image';
import { OnlinePresence, PRESENCE_DOT_RATIO, useVisiblePresence } from './OnlinePresence';

const AvatarImage = (props: AvatarImageProps) => {
  const avatar = useAvatarContext();

  const { hidden, ...arkImageProps } = avatar.getImageProps();

  return (
    <Image
      {...props}
      {...arkImageProps}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        borderRadius: 'var(--avatar-radius)',
        ...props.style,
        // use visibility instead
        visibility: hidden ? 'hidden' : 'visible',
      }}
    />
  );
};

enum Sizes {
  xs2 = '2xs',
  xs = 'xs',
  sm = 'sm',
  md = 'md',
  md2 = '2md',
  lg = 'lg',
  lg2 = '2lg',
  xl = 'xl',
  xxl = 'xxl',
}

const sizes = {
  [Sizes.xs2]: 24,
  [Sizes.xs]: 28,
  [Sizes.sm]: 40,
  [Sizes.md]: 48,
  // Между md и lg: карточке автора 48px мало — лицо перестаёт читаться, а 84px в колонке 300px
  // не оставляют места под ник с флагом.
  [Sizes.md2]: 64,
  [Sizes.lg]: 84,
  // Портрет игрока — карточка автора на заявке и шапка профиля. Больше в колонку 300px не имеет
  // смысла: xl (180px) занимает 72% ширины и без обложки читается как ошибка вёрстки.
  [Sizes.lg2]: 120,
  [Sizes.xl]: 180,
  [Sizes.xxl]: 240,
};

type AvatarProps = Omit<ChakraAvatar.RootProps, 'size'> & {
  name?: string;
  src?: string | null;
  icon?: React.ReactElement;
  fallback?: React.ReactNode;
  ref?: React.RefObject<HTMLDivElement>;
  /**
   * Чей это аватар. Передан — рисуем точку присутствия и добавляем человека в общий опрос
   * статусов; не передан — точки нет. Раньше вместо этого передавалось время последнего визита,
   * из которого точка вычислялась на месте, — см. OnlinePresence.
   */
  userId?: string | null;
  /**
   * Готовый статус для точки — если экран уже получил его вместе со списком. Проброс в
   * OnlinePresence, там же и объяснение, почему передавать надо всегда, включая `null`.
   */
  presenceStatus?: PresenceStatus | null;
  /**
   * Ступень шкалы или адаптивный набор ступеней: строка человека сжимается на телефоне
   * (`{ base: 'sm', md: 'md' }`). Раньше тип допускал только строку, объект проезжал мимо
   * проверки типов и ломал размеры молча.
   */
  size?: `${Sizes}` | Partial<Record<'base' | 'sm' | 'md' | 'lg' | 'xl' | '2xl', `${Sizes}`>>;
};

export const Avatar = ({
  name,
  src,
  icon,
  fallback,
  children,
  ref,
  size = Sizes.md,
  userId,
  presenceStatus,
  bg,
  bgColor,
  ...rest
}: AvatarProps) => {
  /*
    Размер приходит либо строкой, либо адаптивным объектом. Пиксели считаем для каждой ступени
    отдельно и отдаём Chakra как есть — она сама разложит их по брейкпоинтам. Прежний
    `sizes[size]` на объекте отдавал `undefined`, и аватар с точкой оставались без размеров.
  */
  const toPx = (value: `${Sizes}`) => `${sizes[value]}px`;
  /** Доля точки берётся от базовой ступени: у адаптивных размеров строки она одна и та же. */
  const sizeKey = typeof size === 'string' ? size : (size.base ?? Sizes.md);
  const boxSize =
    typeof size === 'string'
      ? toPx(size)
      : Object.fromEntries(
          Object.entries(size as Record<string, `${Sizes}`>).map(([bp, value]) => [bp, toPx(value)])
        );
  const imagePx = sizes[sizeKey];

  /*
    Заливку вынимаем из общих пропов намеренно: она уезжает на внутренний слой, а не на корень,
    и без этого вызывающий потерял бы возможность её переопределить. На этом ловится пустое
    кресло в составе сессии — оно просит `bg: transparent`, чтобы остаться пунктирным контуром,
    и с заливкой на внутреннем слое превратилось бы в серый кружок.
  */
  const fill = bg ?? bgColor ?? (!src && name ? pickAvatarPalette(name) : 'brand.600');

  /*
    Точка присутствия отделяется от лица вырезом, а не кольцом.

    Кольцо было сплошной заливкой цвета той поверхности, на которой лежит аватар, и цвет этот
    проставлял вызывающий — переменной `--status-ring-color`, в десяти местах руками. Связь
    держалась на памяти: поменял материал плиты — обязан вспомнить про кольцо, и в коде рядом с
    переменной успел завестись комментарий-напоминание. Вспоминали не всегда, а местами верного
    значения и не существует: под строкой рекомендованного игрока лежит полупрозрачная подложка,
    а список чатов на телефоне теряет фон целиком.

    Вырез снимает вопрос: сквозь дырку видно ту поверхность, которая под аватаром на самом деле.
    Центр отверстия совпадает с центром точки (90% / 90% — следствие `bottom/right: 10%` и сдвига
    на половину точки), радиус равен диаметру точки: половина уходит под саму точку, половина
    остаётся зазором, ровно как у прежнего кольца. Полпикселя растушёвки — против ступенек.

    Дырку вырезаем только когда точка действительно рисуется: у офлайнового человека её нет, и
    отверстие осталось бы дыркой без причины.
  */
  const presence = useVisiblePresence(userId, presenceStatus);
  /*
    Радиус отверстия равен диаметру точки: половина уходит под саму точку, половина остаётся
    зазором — ровно то, что рисовало прежнее кольцо. Считается в долях аватара, как и точка,
    поэтому адаптивный размер (`{ base: "sm", md: "md" }` у строки человека) обслуживается сам
    собой: раньше такой размер приходил объектом, таблица по нему отдавала `undefined`, и
    отверстие с точкой оставались без размера вовсе.
  */
  const dotPercent = (PRESENCE_DOT_RATIO[sizeKey] ?? 0.25) * 100;
  const cutout = presence
    ? `radial-gradient(circle at 90% 90%, transparent ${dotPercent}cqw, var(--chakra-colors-black) calc(${dotPercent}cqw + 1px))`
    : undefined;

  return (
    <ChakraAvatar.Root
      ref={ref}
      bg="transparent"
      color="gray.fg"
      height={boxSize}
      width={boxSize}
      containerType="inline-size"
      {...rest}
    >
      {/*
        Заливка и картинка переехали внутрь, потому что маска действует на всё поддерево: будь она
        на корне, вырезало бы и саму точку. Корень остаётся тем же элементом, что и раньше, — на
        нём по-прежнему живут рамка, отступы и прочее, что передаёт вызывающий.
      */}
      <Box
        position="absolute"
        inset="0"
        display="flex"
        alignItems="center"
        justifyContent="center"
        /*
          Форма наследуется от корня, а не прибита к кругу.

          Здесь стоял жёсткий `full`, и он совпадал с видом ровно до тех пор, пока аватар
          круглый. У некруглого - строка входящего звонка задаёт ему 28px - внутренний слой
          оставался кругом, и получался кружок с буквой внутри квадратной рамки, между ними
          зазор в углах. Видно это было только на аватаре без картинки: у картинки своя форма
          приходит через `--avatar-radius`, а фон и буква оставались круглыми.
        */
        borderRadius="inherit"
        overflow="hidden"
        bg={fill}
        css={cutout ? { maskImage: cutout, WebkitMaskImage: cutout } : undefined}
      >
        {!src && (
          <ChakraAvatar.Fallback name={name ?? ' '} fontSize="40cqw">
            {fallback || icon}
          </ChakraAvatar.Fallback>
        )}
        {src && <AvatarImage src={src} width={imagePx} height={imagePx} />}
      </Box>
      {children}
      {userId ? (
        <OnlinePresence
          userId={userId}
          status={presenceStatus}
          position="absolute"
          bottom="10%"
          right="10%"
          transform="translate(50%,50%)"
          size={sizeKey}
        />
      ) : null}
    </ChakraAvatar.Root>
  );
};

export const AvatarGroup = ChakraAvatarGroup;
